import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, createVoucher, getAppSettings } from '../../api';
import { useAppSettings } from '../../contexts/app-settings-ctx';
import {
  Text,
  Heading,
  VStack,
  Button,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react';
import { ModalInfo } from '../../components/ModalInfo';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Navigate, Link } from 'react-router-dom';
import VoucherSelector from './VoucherSelector';
import ModalSpinner from '../../components/ModalSpinner';
import Footer from '../../components/Footer';
import CustomDatePicker from '../../components/CustomDatePicker';

const formatDate = (date) => {
  if (!date) return '';
  const options = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
  return new Intl.DateTimeFormat('it', options).format(new Date(date));
};

const vouchers = [
  { text: '10€', id: '1', value: { v: 10, type: 'euro' } },
  { text: '15€', id: '2', value: { v: 15, type: 'euro' } },
  { text: '20€', id: '3', value: { v: 20, type: 'euro' } },
  { text: '30€', id: '4', value: { v: 30, type: 'euro' } },
  { text: '50€', id: '5', value: { v: 50, type: 'euro' } },
  { text: '100€', id: '6', value: { v: 100, type: 'euro' } },
  { text: '200€', id: '7', value: { v: 200, type: 'euro' } },
  { text: '10%', id: '8', value: { v: 10, type: 'percentage' } },
  { text: '15%', id: '9', value: { v: 15, type: 'percentage' } },
  { text: '21%', id: '10', value: { v: 21, type: 'percentage' } },
];

function Voucher() {
  const [user] = useAuthState(auth);
  const { appSettings, setAppSettings, reset } = useAppSettings();

  const [isLoading, setIsLoading] = useState(false);
  const [voucher, setVoucher] = useState('');
  const [quantity, setQuantity] = useState(0);
  const defaultExpiration = new Date();
  defaultExpiration.setDate(defaultExpiration.getDate() + 30);
  const [expirationDate, setExpirationDate] = useState(defaultExpiration);
  const [showModalConfirm, setShowModalConfirm] = useState(false);
  const [showModalSuccess, setShowModalSuccess] = useState(false);
  const [showModalError, setShowModalError] = useState(false);
  const [voucherCreated, setVoucherCreated] = useState([]);
  const [error, setError] = useState('');

  // Load app settings
  useEffect(() => {
    const _getAppSettings = async () => {
      setIsLoading(true);
      const settings = await getAppSettings();
      setAppSettings({ ...settings });
      setIsLoading(false);
    };
    reset();
    _getAppSettings();
  }, []);

  const createVoucherHandler = async () => {
    setIsLoading(true);
    const res = await createVoucher(voucher, quantity, expirationDate);
    if (res.success) {
      setVoucherCreated([...res.data]);
      setShowModalSuccess(true);
    } else {
      setVoucherCreated([]);
      setError(res.error);
      setShowModalError(true);
    }
    setIsLoading(false);
  };

  if (!user || (user.displayName !== 'admin' && user.displayName !== 'promoter'))
    return <Navigate to='/dashboard' replace={true} />;

  return (
    <VStack w='100%'>
      <Heading mt={5} mb={0} color={'white'}>
        Creazione Voucher
      </Heading>

      <Text mb={3}>{process.env.REACT_APP_PROJECT_ID}</Text>
      <Button mb={3} as={Link} to='/dashboard'>
        Dashboard
      </Button>

      <VStack border='3px solid teal' borderRadius={15} p={3} w={'100%'} alignItems={'flex-start'}>
        <Text>Seleziona il valore del voucher</Text>
        <VoucherSelector
          options={vouchers}
          onChange={(value) => {
            const voucher = vouchers.find((v) => v.id == value);
            setVoucher(voucher);
          }}
        />

        <Text>Seleziona il numero di voucher da creare (max 100)</Text>
        <NumberInput
          bg='#2a3745'
          defaultValue={quantity}
          min={0}
          max={100}
          borderRadius={25}
          boxShadow='md'
          onChange={(val) => setQuantity(parseInt(val))}
        >
          <NumberInputField />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>

        <Text>Seleziona la data di scadenza</Text>
        <DatePicker
          // showTimeSelect
          dateFormat='MMMM d, yyyy'
          minDate={new Date()}
          showPopperArrow={false}
          selected={expirationDate}
          onChange={(date) => {
            if (date) {
              const endOfDay = new Date(date);
              endOfDay.setHours(23, 59, 59, 999);
              setExpirationDate(endOfDay);
            }
          }}
          customInput={<CustomDatePicker />}
        />
        <br />
        <br />
        <Button
          isDisabled={!voucher || quantity === 0 || !appSettings.hasVoucher || !expirationDate}
          isLoading={isLoading}
          onClick={() => setShowModalConfirm(true)}
        >
          Crea Voucher
        </Button>
        {!appSettings.hasVoucher && (
          <Text color='tomato'>Questa dashboard non è abilitata per la creazione dei voucher</Text>
        )}
      </VStack>

      <ModalSpinner isOpen={isLoading} />
      <Footer />

      <ModalInfo
        title='Conferma'
        body={`Confermi la creazione di ${quantity} voucher da ${
          voucher.text
        } con scadenza il ${formatDate(expirationDate)}?`}
        isOpen={showModalConfirm}
        onCancel={() => setShowModalConfirm(false)}
        onClose={() => {
          setShowModalConfirm(false);
          createVoucherHandler();
        }}
      />

      <ModalInfo title='Info' isOpen={showModalSuccess} onClose={() => setShowModalSuccess(false)}>
        <Text>Voucher creati con successo.</Text>
        <br />
        <Text>
          I voucher scadranno il giorno <b>{formatDate(voucherCreated[0]?.expirationDate)}.</b>
        </Text>
        <br />
        <Text>
          Valore dei voucher <b>{voucher.text}</b>
        </Text>
        <br />
        <Text color='gold'>Copia i codici prima di chiudere questa finestra.</Text>
        <br />
        {voucherCreated.map((_voucher) => (
          <Text key={_voucher.code}>{_voucher.code}</Text>
        ))}
      </ModalInfo>

      <ModalInfo
        title='Errore'
        isOpen={showModalError}
        body={`C'è stato un errore nella creazione dei voucher: ${error}`}
        onClose={() => setShowModalError(false)}
      />
    </VStack>
  );
}

export default Voucher;
